import {
  savedViewListResponseSchema,
  savedViewSchema,
  type CreateSavedViewRequest,
  type SavedView,
  type SavedViewListResponse,
  type UpdateSavedViewRequest,
  type SavedViewScreen,
} from '@complan/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from '@/shared/api/client';
import { queryKeys } from '@/shared/query/query-keys';

/**
 * The Saved_View data layer (task 22.6, wired to the task 17.7 API).
 *
 * Follows `calendar-data.ts`'s hook style: a thin `apiFetch` per endpoint, the
 * wire body parsed through the contracts schema so a drifted shape fails at the
 * boundary, and a React Query hook per read. The writes are mutations that
 * invalidate the one `savedViews` key on success, so a create, rename, share,
 * unpublish or delete is reflected in the menu without a manual refetch.
 *
 * A Saved_View stores the same filter grammar the calendar and the export use, so
 * opening one is a pure re-serialisation to the URL (see `savedViewToView` in the
 * URL codec) — this module only fetches and mutates the stored views; the
 * *applying* of a view to the screen is the URL's job.
 *
 * The stale-reference facts the read carries — `deactivatedFilterValues` (R11.15)
 * and `omittedFilterValueCount` (R11.16) — are already resolved by the API
 * (`partitionStoredReferences`), so nothing here recomputes them; the menu reads
 * them straight off the fetched view to show the indication.
 */

/**
 * Fetches the requester's own Saved_Views plus the shared ones, with the ownership cap.
 *
 * `screen` narrows to the views belonging to one screen (`complan-governance` R6.10).
 * A screen always passes its own: without it a Governance_Dashboard would offer
 * calendar layouts it cannot render, and the calendar would offer dashboard ones.
 */
export async function fetchSavedViews(screen: SavedViewScreen): Promise<SavedViewListResponse> {
  const body = await apiFetch<unknown>(`/api/saved-views?screen=${encodeURIComponent(screen)}`);
  return savedViewListResponseSchema.parse(body);
}

/** React Query hook for the Saved_View list of one screen (R11.10, R11.12, R6.10). */
export function useSavedViews(
  screen: SavedViewScreen,
): UseQueryResult<SavedViewListResponse, unknown> {
  return useQuery({
    // Keyed by screen, or two screens' menus would share one cache entry and each
    // would show the other's views.
    queryKey: queryKeys.savedViews(screen),
    queryFn: () => fetchSavedViews(screen),
  });
}

/** Creates a Saved_View from the current filter combination (R11.10). */
export function useCreateSavedView(): UseMutationResult<SavedView, unknown, CreateSavedViewRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateSavedViewRequest) => {
      const body = await apiFetch<unknown>('/api/saved-views', {
        method: 'POST',
        body: JSON.stringify(request),
      });
      return savedViewSchema.parse(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.savedViewsAll() });
    },
  });
}

/**
 * Edits a Saved_View: rename, share (publish) and unpublish all go through the
 * same `PUT /api/saved-views/:id` (R11.11, R11.12, R11.14), since each is a change
 * to the stored `name`, `isShared` or filter/orientation/sort. The caller sends
 * the whole write shape; sharing is `isShared: true`, unpublishing `false`.
 */
export function useUpdateSavedView(): UseMutationResult<
  SavedView,
  unknown,
  { readonly id: string; readonly request: UpdateSavedViewRequest }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, request }) => {
      const body = await apiFetch<unknown>(`/api/saved-views/${id}`, {
        method: 'PUT',
        body: JSON.stringify(request),
      });
      return savedViewSchema.parse(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.savedViewsAll() });
    },
  });
}

/** Deletes a Saved_View (R11.14: owner or Commercial_Planning only, enforced by the API). */
export function useDeleteSavedView(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiFetch<unknown>(`/api/saved-views/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.savedViewsAll() });
    },
  });
}
